import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { centsToMajorString, formatCentsString, majorStringToCents } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface CashShift {
  id: string;
  initial_fund_cents: string;
  opened_at: string;
}

interface CashCutDialogProps {
  shift: CashShift;
  submitting: boolean;
  on_close: () => void;
  on_submit: (input: {
    declared_cash_cents: string;
    declared_credit_card_cents: string;
    declared_debit_card_cents: string;
    notes: string;
  }) => void | Promise<void>;
}

export function CashCutDialog({
  shift,
  submitting,
  on_close,
  on_submit,
}: CashCutDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [amount_major, setAmountMajor] = useState('0.00');
  const [credit_major, setCreditMajor] = useState('0.00');
  const [debit_major, setDebitMajor] = useState('0.00');
  const [notes, setNotes] = useState('');

  const declared_cash_cents = useMemo(() => {
    try {
      return majorStringToCents(amount_major || '0').toString();
    } catch {
      return '';
    }
  }, [amount_major]);
  const declared_credit_card_cents = useMemo(() => {
    try {
      return majorStringToCents(credit_major || '0').toString();
    } catch {
      return '';
    }
  }, [credit_major]);
  const declared_debit_card_cents = useMemo(() => {
    try {
      return majorStringToCents(debit_major || '0').toString();
    } catch {
      return '';
    }
  }, [debit_major]);
  const can_submit =
    declared_cash_cents.length > 0 &&
    declared_credit_card_cents.length > 0 &&
    declared_debit_card_cents.length > 0 &&
    !submitting;

  const onClickSubmit = async (): Promise<void> => {
    if (!can_submit) return;
    await on_submit({
      declared_cash_cents,
      declared_credit_card_cents,
      declared_debit_card_cents,
      notes,
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('cash_shifts.close_dialog_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="border-border bg-card/40 rounded-md border p-3 text-xs">
            <div>
              <span className="text-muted-foreground">{t('cash_shifts.initial_fund')}: </span>
              <span className="font-mono">{formatCentsString(shift.initial_fund_cents)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('cash_shifts.shift_id')}: </span>
              <span className="font-mono">{shift.id.slice(0, 8)}</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="close-amount">{t('cash_shifts.declared_cash')}</Label>
            <Input
              id="close-amount"
              inputMode="decimal"
              className="font-mono"
              value={amount_major}
              onChange={(e) => setAmountMajor(e.target.value)}
              disabled={submitting}
            />
            <p className="text-muted-foreground text-xs">
              {t('cash_shifts.amount_hint', {
                value: centsToMajorString(declared_cash_cents || '0'),
              })}
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="close-debit">{t('cash_shifts.declared_debit_card')}</Label>
            <Input
              id="close-debit"
              inputMode="decimal"
              className="font-mono"
              value={debit_major}
              onChange={(e) => setDebitMajor(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="close-credit">{t('cash_shifts.declared_credit_card')}</Label>
            <Input
              id="close-credit"
              inputMode="decimal"
              className="font-mono"
              value={credit_major}
              onChange={(e) => setCreditMajor(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="close-notes">{t('cash_shifts.notes')}</Label>
            <textarea
              id="close-notes"
              rows={2}
              className="border-input bg-background flex w-full rounded-md border px-3 py-2 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
            />
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {submitting ? t('common.loading') : t('cash_shifts.close_shift')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
